import {PieChartWithLabelsRow} from "../../../../dashboard/widgets/charts/PieChartWithLabels";
import {LegendItem} from "../../../../components/charts/Legend";

export interface CoffeeMachineConsumption {
    boilerTemperature: number;
    date: string;
    dispensingGroup: number;
    dispensingTime: number;
    pumpPressure: number;
    temperature: number;
    waterFlowSpeed: number;
    boilerServicesPressure: number;
    continueConsumption: boolean | null;
}

export interface ThresholdFrequency {
    thresholdCode: number;
    count: number;
}

export interface CoffeeConsumptionByType {
    consumptionTypeCode: number;
    consumptionTypeName: string;
    continueConsumption: boolean;
    count: number;
    distribution: ThresholdFrequency[];
}

export interface CoffeeConsumptionStatistics {
    [statisticCode: string]: CoffeeConsumptionByType[];
}

export interface CoffeeMachineAvgConsumption {
    boilerTemperature: number;
    dispensingTime: number;
    pumpPressure: number;
    temperature: number;
    waterFlowSpeed: number;
    boilerServicesPressure: number;
}

export class CoffeeMachineJsonMapper {
    public static toAvgConsumption(consumptions: CoffeeMachineConsumption[]): CoffeeMachineAvgConsumption {
        const avgConsumption: CoffeeMachineAvgConsumption = {
            boilerTemperature: null,
            dispensingTime: null,
            pumpPressure: null,
            temperature: null,
            waterFlowSpeed: null,
            boilerServicesPressure: null,
        };
        if (consumptions.length === 0) {
            return avgConsumption;
        }
        let boilerTemperatureSum = 0;
        let boilerTemperatureCount = 0;
        let dispenseTimeSum = 0;
        let dispenseTimeCount = 0;
        let pumpPressureSum = 0;
        let pumpPressureCount = 0;
        let temperatureSum = 0;
        let temperatureCount = 0;
        let waterFlowSpeedSum = 0;
        let waterFlowSpeedCount = 0;
        let boilerServicesPressureSum = 0;
        let boilerServicesPressureCount = 0;

        consumptions.forEach(
            (c: CoffeeMachineConsumption) => {
                if (c.boilerTemperature) {
                    boilerTemperatureSum += c.boilerTemperature;
                    boilerTemperatureCount++;
                }
                if (c.dispensingTime) {
                    dispenseTimeSum += c.dispensingTime;
                    dispenseTimeCount++;
                }
                if (c.pumpPressure) {
                    pumpPressureSum += c.pumpPressure;
                    pumpPressureCount++;
                }
                if (c.temperature) {
                    temperatureSum += c.temperature;
                    temperatureCount++;
                }
                if (c.waterFlowSpeed) {
                    waterFlowSpeedSum += c.waterFlowSpeed;
                    waterFlowSpeedCount++;
                }
                if (c.boilerServicesPressure) {
                    boilerServicesPressureSum += c.boilerServicesPressure;
                    boilerServicesPressureCount++;
                }
            },
        );
        avgConsumption.boilerTemperature =
            boilerTemperatureCount > 0 ? boilerTemperatureSum / boilerTemperatureCount : null;
        avgConsumption.dispensingTime =
            dispenseTimeCount > 0 ? dispenseTimeSum / dispenseTimeCount : null;
        avgConsumption.pumpPressure =
            pumpPressureCount > 0 ? pumpPressureSum / pumpPressureCount : null;
        avgConsumption.temperature =
            temperatureCount > 0 ? temperatureSum / temperatureCount : null;
        avgConsumption.waterFlowSpeed =
            waterFlowSpeedCount > 0 ? waterFlowSpeedSum / waterFlowSpeedCount : null;
        avgConsumption.boilerServicesPressure =
            boilerServicesPressureCount > 0 ? boilerServicesPressureSum / boilerServicesPressureCount : null;
        return avgConsumption;
    }

    public static filterConsumptions(data: CoffeeConsumptionByType[], continueConsumption: boolean): number {
        let consumptions: CoffeeConsumptionByType;
        if (continueConsumption) {
            consumptions = data.filter((d) => d.continueConsumption != null && d.continueConsumption)[0];
        } else if (continueConsumption != null && continueConsumption === false) {
            consumptions = data.filter((d) => d.continueConsumption != null && !d.continueConsumption)[0];
        } else {
            consumptions = data.filter((d) => d.continueConsumption == null)[0];
        }
        return consumptions === undefined ? 0 : consumptions.count;
    }

    public static toConsumptionByType(statistics: CoffeeConsumptionStatistics,
                                      legendItems: LegendItem[]): PieChartWithLabelsRow[] {
        const statisticsWithData = Object.keys(statistics).filter((s) => statistics[s] != null);
        let consumptions: CoffeeConsumptionByType[] = [];
        if (statisticsWithData.length !== null) {
            consumptions = statistics[statisticsWithData[0]];
        }
        const continueConsumptions: PieChartWithLabelsRow = {
            code: legendItems[0].code,
            label: legendItems[0].name,
            frequency: this.filterConsumptions(consumptions, true),
            color: legendItems[0].color,
        };
        const configuredConsumptions: PieChartWithLabelsRow = {
            code: legendItems[1].code,
            label: legendItems[1].name,
            frequency: this.filterConsumptions(consumptions, false),
            color: legendItems[1].color,
        };
        const unknownConsumptions: PieChartWithLabelsRow = {
            code: legendItems[2].code,
            label: legendItems[2].name,
            frequency: this.filterConsumptions(consumptions, null),
            color: legendItems[2].color,
        };
        return [continueConsumptions, configuredConsumptions, unknownConsumptions];
    }
}
